<template>
  <div class="container mx-auto px-4 pt-40 pb-32 max-w-2xl text-center">
    <div v-if="status === 'succeeded'" class="space-y-6">
      <div
        class="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto"
      >
        <UIcon name="i-lucide-check" class="w-12 h-12 text-green-600" />
      </div>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Paiement réussi !
      </h1>
      <p class="text-gray-600 dark:text-gray-300 text-lg">
        Merci pour votre commande. Vous recevrez un email de confirmation dans
        quelques instants.
      </p>
      <div class="pt-8">
        <UButton to="/" color="main" size="xl"> Retour à l'accueil </UButton>
      </div>
    </div>

    <div v-else-if="status === 'processing'" class="space-y-6">
      <UIcon
        name="i-lucide-loader-2"
        class="w-16 h-16 animate-spin text-main mx-auto"
      />
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
        Traitement en cours...
      </h1>
      <p class="text-gray-600 dark:text-gray-300">
        Votre paiement est en cours de validation.
      </p>
    </div>

    <div v-else class="space-y-6">
      <div
        class="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto"
      >
        <UIcon name="i-lucide-x" class="w-12 h-12 text-red-600" />
      </div>
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
        Échec du paiement
      </h1>
      <p class="text-red-600 dark:text-red-400 font-medium">
        {{
          errorMsg ||
          "Une erreur est survenue lors du paiement. Veuillez réessayer."
        }}
      </p>
      <div class="pt-8">
        <UButton to="/checkout" color="main" size="xl">
          Retour au paiement
        </UButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Repas } from "~/models/repas";

const route = useRoute();
const router = useRouter();
const cartStore = useCartStore();
const authStore = useAuthStore();
const config = useRuntimeConfig();
const status = ref<string>("loading");
const errorMsg = ref("");

// Helper to parse original ingredients
const parseOriginalIngredients = (meal: Repas): Record<string, number> => {
  const originalIngredients: Record<string, number> = {};

  if (meal.content && Array.isArray(meal.content) && meal.content.length > 0) {
    meal.content.forEach(
      (item: { ingredient?: { name: string }; quantity: number }) => {
        if (item.ingredient && item.ingredient.name) {
          originalIngredients[item.ingredient.name.toLowerCase()] =
            item.quantity;
        }
      },
    );
    return originalIngredients;
  }

  if (meal.ingredients) {
    const ingredientStrings = meal.ingredients
      .split(",")
      .map((s: string) => s.trim());
    ingredientStrings.forEach((ingredientString: string) => {
      const match = ingredientString.match(/^(\d+)\s+(.+)$/);
      if (match) {
        const quantity = parseInt(match[1]);
        const ingredientName = match[2].trim();
        originalIngredients[ingredientName.toLowerCase()] = quantity;
      } else if (ingredientString.length > 0) {
        const ingredientName = ingredientString.trim();
        originalIngredients[ingredientName.toLowerCase()] = 1;
      }
    });
  }
  return originalIngredients;
};

// Helper to calculate modifications
const getModifications = (
  meal: Repas,
  extras: Array<{
    ingredient: { name: string; price: number };
    quantity: number;
  }> = [],
) => {
  const original = parseOriginalIngredients(meal);
  const added: Array<{ name: string; quantity: number; price: number }> = [];
  const removed: Array<{ name: string }> = [];

  // Create a map of extras for easier lookup
  const extrasMap = new Map(
    extras.map((e) => [e.ingredient.name.toLowerCase(), e]),
  );

  // Check for Added (In extras but not in original, or quantity increased)
  extras.forEach((extra) => {
    const lowerName = extra.ingredient.name.toLowerCase();
    const originalQty = original[lowerName] || 0;
    if (extra.quantity > originalQty) {
      added.push({
        name: extra.ingredient.name,
        quantity: extra.quantity - originalQty,
        price: extra.ingredient.price,
      });
    }
  });

  // Check for Removed (In original but not in extras, or quantity reduced)
  Object.entries(original).forEach(([name, qty]) => {
    const extraItem = extrasMap.get(name);
    if (extraItem) {
      if (extraItem.quantity < qty) {
        removed.push({ name: name });
      }
    } else {
      // If customization UI allows removing by unchecking, then missing might mean removed.
      // But if it allows configuring quantity, 0 means removed.
      // For safe logic given ambiguity, we'll stick to explicit quantity checks if possible,
      // or just ignore if we can't be sure without more extensive logic.
      // Assuming extras list is comprehensive for customized items:
      // if (!extraItem) removed.push({ name }); // Potentially risky if extras is partial
    }
  });

  return { added, removed };
};

onMounted(async () => {
  const redirectStatus = route.query.redirect_status as string;
  const paymentIntentId = route.query.payment_intent as string;

  if (redirectStatus === "succeeded") {
    status.value = "processing";

    try {
      // Construct payload
      const itemsPayload = cartStore.items.map((item) => {
        const itemId = item.type === "menu" ? item.menu?.id : item.meal?.id;
        const unitPrice =
          item.quantity > 0 ? item.totalPrice / item.quantity : 0;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const options: any = {
          note: item.note, // Pass item note
        };

        if (item.type === "regular" && item.meal) {
          options.name = item.meal.name;
        }

        if (item.type === "customized" && item.meal) {
          options.name = item.meal.name;
          const { added, removed } = getModifications(item.meal, item.extras);
          options.added = added;
          options.removed = removed;
          options.extras = item.extras; // Keep raw just in case
        }

        if (item.type === "menu" && item.menu) {
          options.name = item.menu.name;
          options.selectedSauces = item.selectedSauces;
          options.selectedDesserts = item.selectedDesserts;

          if (item.menuCustomizations) {
            options.menuCustomizations = item.menuCustomizations.map((c) => {
              // Find the meal in the menu content to get its metadata
              const content = item.menu?.menu_content.find(
                (mc) => mc.id === c.menuContentId,
              );
              const meal = content?.repas;
              let mods = { added: [], removed: [] };
              if (meal) {
                mods = getModifications(meal, c.extras);
              }

              return {
                ...c,
                mealName: c.mealName || meal?.name || "Plat",
                repasId: meal?.id || 0,
                added: mods.added,
                removed: mods.removed,
              };
            });
          }
        }

        return {
          itemType: item.type,
          itemId: itemId || 0,
          quantity: item.quantity,
          price: unitPrice,
          options: options,
        };
      });

      // Call API
      const order = await $fetch<{ id: number }>(
        `${
          config.public.apiUrl || "http://localhost:4200/api"
        }/cart-history/finalize`,
        {
          method: "POST",
          body: {
            userId:
              authStore.isLoggedIn() && authStore.user
                ? authStore.user.id
                : null,
            paymentIntentId: paymentIntentId,
            total: cartStore.totalPrice,
            items: itemsPayload,
            guestInfo: cartStore.deliveryInfo
              ? {
                  firstName: cartStore.deliveryInfo.firstName,
                  lastName: cartStore.deliveryInfo.lastName,
                  phone: cartStore.deliveryInfo.phone,
                  email: cartStore.deliveryInfo.email || "",
                  address: {
                    street:
                      cartStore.deliveryInfo.address?.street ||
                      cartStore.deliveryInfo.street,
                    postalCode:
                      cartStore.deliveryInfo.address?.postalCode ||
                      cartStore.deliveryInfo.postalCode,
                    city:
                      cartStore.deliveryInfo.address?.city ||
                      cartStore.deliveryInfo.city,
                    instructions:
                      cartStore.deliveryInfo.address?.instructions ||
                      cartStore.deliveryInfo.instructions,
                  },
                }
              : undefined,
          },
        },
      );

      // Capture email + phone before clearing cart
      const trackEmail = cartStore.deliveryInfo?.email || "";
      const trackPhone = cartStore.deliveryInfo?.phone || "";

      // Success
      cartStore.clearCart();
      status.value = "succeeded";
      setTimeout(() => {
        router.push({
          path: `/orders/${order.id}`,
          query: { email: trackEmail, phone: trackPhone },
        });
      }, 1500);
    } catch (e) {
      console.error("Order finalization failed:", e);
      status.value = "failed";
      errorMsg.value =
        "Erreur lors de la création de la commande. Veuillez contacter le support.";
    }
  } else if (redirectStatus === "processing") {
    status.value = "processing";
  } else {
    status.value = "failed";
  }
});
</script>
